// useStreak — consecutive UTC days the current user logged any Hub activity.
// Now backed by the get_streak_state RPC, which also reports:
//   - has_freeze_available: free weekly freeze still unused this ISO week
//   - at_risk: streak > 0 but no activity logged today (UTC)
//   - protected_day: the most recent gap day a freeze covered (if any)
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export const useStreak = () => {
  const { user } = useAuth();
  const [streak, setStreak] = useState(0);
  const [hasFreezeAvailable, setHasFreezeAvailable] = useState(false);
  const [atRisk, setAtRisk] = useState(false);
  const [protectedDay, setProtectedDay] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setStreak(0);
      setHasFreezeAvailable(false);
      setAtRisk(false);
      setProtectedDay(null);
      setLoading(false);
      return;
    }
    const { data } = await supabase.rpc("get_streak_state", { _user_id: user.id });
    const row = Array.isArray(data) ? data[0] : null;
    setStreak(row?.current_streak ?? 0);
    setHasFreezeAvailable(!!row?.has_freeze_available);
    setAtRisk(!!row?.at_risk);
    setProtectedDay(row?.protected_day ?? null);
    setLoading(false);
  }, [user?.id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Consume the weekly free freeze (no-ops if no gap or already used).
  const applyFreeze = useCallback(async () => {
    const { data, error } = await supabase.rpc("apply_streak_freeze");
    const row = Array.isArray(data) ? data[0] : null;
    if (!error && row?.ok) {
      await refresh();
    }
    return { ok: !!row?.ok, reason: row?.reason as string | undefined };
  }, [refresh]);

  return { streak, loading, hasFreezeAvailable, atRisk, protectedDay, applyFreeze, refresh };
};
